import {
  defineNuxtRouteMiddleware,
  setPageLayout,
  useSeoMeta,
  navigateTo,
  useHeadSafe,
  useHead,
  createError
} from "#imports";
import { useCMSContent } from "#nk/composables/cms";
import { rand } from "@vueuse/core";
import { DateTime } from "luxon";
export default defineNuxtRouteMiddleware(async (to, _from) => {
  const page = await useCMSContent(to);
  if (page == null || page != null && page.value.hasError) {
    return;
  }
  if (page.value.apiCallsContent && Object.values(page.value.apiCallsContent).length === 1 && Object.values(page.value.apiCallsContent).every(
    (value) => value === null || value === void 0 || value === ""
  )) {
    throw createError({
      statusCode: 404,
      statusMessage: "Page not found"
    });
  }
  if (page.value.isRedirect && page.value.redirectTo != null) {
    return navigateTo(
      {
        path: page.value.redirectTo,
        hash: to.hash,
        query: {
          ...to.query,
          // cache buster, because cache prevents the redirect if the page is navigated from breadcrumb
          cb: Math.floor(Date.now() / 1e3) + rand(0, 1e3)
        }
      },
      { replace: true }
    );
  }
  if (page && page.value && typeof page.value.theme === "string") {
    setPageLayout(page.value.theme);
  }
  useSeoMeta({
    title: page.value.title,
    description: page.value.description
  });
  if (page.value.theme !== "default") {
    useHeadSafe({
      bodyAttrs: {
        class: `nk-theme-${page.value.theme}`
      }
    });
  }
  if (page.value.context && Array.isArray(page.value.context)) {
    const template = page.value.context.find(
      (obj) => obj.template === "TemplateVeranstaltungenDetail"
    );
    const event = page.value.apiCallsContent.veranstaltung;
    if (template != null && event != null) {
      const startDateTime = DateTime.fromISO(event.datum_von, {
        zone: "Europe/Berlin"
      });
      const endDateTime = DateTime.fromISO(event.datum_bis, {
        zone: "Europe/Berlin"
      });
      let startDate;
      let endDate;
      const isSameDay = startDateTime.hasSame(endDateTime, "day");
      const isMidnight = (dt) => dt.hour === 0 && dt.minute === 0 && dt.second === 0;
      if (!isSameDay) {
        startDate = startDateTime.toFormat("yyyy-MM-dd");
        endDate = endDateTime.toFormat("yyyy-MM-dd");
      } else {
        if (isMidnight(startDateTime)) {
          startDate = startDateTime.toFormat("yyyy-MM-dd");
        } else {
          startDate = startDateTime.toISO();
        }
        if (isMidnight(endDateTime)) {
          endDate = endDateTime.toFormat("yyyy-MM-dd");
        } else {
          endDate = endDateTime.toISO();
        }
      }
      const googleHeader = {
        "@context": "https://schema.org",
        "@type": "Event",
        name: event.titel,
        startDate,
        endDate,
        eventStatus: "https://schema.org/EventScheduled",
        image: [event.bild],
        description: event.plain_text
      };
      if (event.veranstalter) {
        googleHeader.organizer = {
          "@type": "Organization",
          name: event.veranstalter
        };
      } else if (event.brancheneintrag_veranstalter) {
        googleHeader.organizer = {
          "@type": "Organization",
          name: event.brancheneintrag_veranstalter.titel,
          url: event.brancheneintrag_veranstalter.kontakt.link,
          image: event.brancheneintrag_veranstalter.bild,
          email: event.brancheneintrag_veranstalter.kontakt.email,
          telephone: event.brancheneintrag_veranstalter.kontakt.telefon,
          address: {
            "@type": "PostalAddress",
            streetAddress: event.brancheneintrag_veranstalter.adresse.strasse,
            addressLocality: event.brancheneintrag_veranstalter.adresse.ort,
            postalCode: event.brancheneintrag_veranstalter.adresse.plz,
            addressCountry: "DE"
          }
        };
      }
      if (event.veranstaltungsort) {
        googleHeader.location = {
          "@type": "Place",
          name: event.veranstaltungsort,
          address: {
            "@type": "PostalAddress",
            streetAddress: event.adresse.strasse,
            addressLocality: event.adresse.ort,
            postalCode: event.adresse.plz,
            addressCountry: "DE"
          }
        };
      } else if (event.brancheneintrag_veranstaltungsort) {
        googleHeader.location = {
          "@type": "Place",
          name: event.brancheneintrag_veranstaltungsort.titel,
          url: event.brancheneintrag_veranstaltungsort.kontakt.link,
          image: event.brancheneintrag_veranstaltungsort.bild,
          telephone: event.brancheneintrag_veranstaltungsort.kontakt.telefon,
          address: {
            "@type": "PostalAddress",
            streetAddress: event.brancheneintrag_veranstaltungsort.adresse.strasse,
            addressLocality: event.brancheneintrag_veranstaltungsort.adresse.ort,
            postalCode: event.brancheneintrag_veranstaltungsort.adresse.plz,
            addressCountry: "DE"
          }
        };
      } else {
        googleHeader.location = {
          "@type": "Place",
          address: {
            "@type": "PostalAddress",
            addressCountry: "DE"
          }
        };
      }
      const jsonGoogleHeader = JSON.stringify(googleHeader);
      useHead({
        script: [
          {
            hid: "structureddata",
            type: "application/ld+json",
            innerHTML: jsonGoogleHeader
          }
        ]
      });
    }
  }
});
